import { lazy, useContext, useEffect, useMemo } from "react"
import { useIdleTimer } from "react-idle-timer"
import { Provider, useDispatch, useSelector } from "react-redux"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import { useNavigation } from "modules/navigation"
import { OverlayContext, useDialogs } from "modules/overlays"
import { useDataCompany, useGlobe } from "modules/sites"
import { useAuth, useUser } from "modules/users"
import { globalUiAction } from "services/globalUi"
import configureStore from "services/rootStore"
import { userStateAction } from "services/userState"
import { supported } from "services/utils"

import "assets/fonts/fonts.scss"
import "views/styles/layout.scss"

const Search = lazy(() => import("views/routes/Search/Search"))
const Store = lazy(() => import("views/routes/Store/Store"))
const Product = lazy(() => import("views/routes/Product/Product"))
const Scooter = lazy(() => import("views/routes/Scooter/Scooter"))
const TestRide = lazy(() => import("views/routes/TestRide/TestRide"))
const BatteryBill = lazy(() => import("views/routes/Transfer/BatteryBill"))

const QuickMenu = lazy(() => import("views/Retail/QuickMenu"))
const ProspectPages = lazy(() => import("views/Prospect/ProspectPages"))
const ProspectComponent = lazy(() => import("views/Prospect/ProspectComponent"))
const ExhibitionsPage = lazy(() => import("views/Exhibition/ExhibitionsPage"))
const AllocationPages = lazy(() => import("views/Allocation/AllocationPages"))
const OrderRoutes = lazy(() => import("views/Order/OrderRoutes"))
const TransferRoutes = lazy(() => import("views/Transfer/TransferRoutes"))
const ConfigRoutes = lazy(() => import("views/Config/ConfigRoutes"))
const LoansReport = lazy(() => import("views/Report/LoansReport"))
const PointsReport = lazy(() => import("views/Report/PointsReport"))
const ScooterPage = lazy(() => import("views/Scooter/ScooterPage"))
const MyProfileRoutes = lazy(() => import("views/MyProfile"))

const StripTW = () => {
  const { navigateReplace } = useNavigation()
  const location = useLocation()
  useEffect(() => {
    navigateReplace(Object.assign({}, location, { pathname: location.pathname.replace(/^\/tw/, "") }))
  }, [navigateReplace, location])
  return null
}

const iconsMap = {
  "/testride": "smile",
  "/prospect": "user",
  "/scooter": "star",
  "/target-achievement": "activity",
  "/store": "shopping-bag",
  "/ownership/transfer": "copy",
  "/ownership/batterybill": "battery",
  "/orders": "file",
  "/allocations": "truck",
  "/configs": "settings",
  "/report": "file-text",
  "/orders/groups": "server",
  "/scooters": "navigation_scooter",
  "/my-profile": "user",
}

const menuIdMap = new Map([
  ["2e84054c-b2b1-40e7-86da-565fd290ff7a", "nav.testride"],
  ["cac4aeb4-6ba6-4926-8bdf-06a1c42df463", "nav.prospect"],
  ["ff4c7fff-ae9c-4031-bcdd-aa76f79e4e36", "nav.scooter"],
  ["41b04ccf-fa04-4a47-93e0-5d9b58420031", "nav.order"],
  ["361e13ba-38eb-4e33-b7a2-9f17f2247d26", "nav.achivement"],
  ["a2a0981e-93c3-487f-a4bc-684b57abde79", "nav.acc"],
  ["d4029eb8-3ac8-4435-b05d-b0dba5134d4a", "nav.transfer"],
  ["a2d76c91-b3c0-44db-a6ba-0092e68b18af", "nav.subscribe"],
  ["c27cbdab-e4cf-44c6-ab1e-917d91513d7d", "nav.report"],
  ["90860989-ce01-4d04-ace3-f8f99a69400c", "nav.testride_walkin"],
  ["be0e439c-514b-452e-a535-161f3887cc96", "nav.testride_visitonly"],
  ["80a5249f-cab6-475c-be2d-52be10146619", "nav.dashboard"],
  ["2f775558-844a-4e8c-8e39-57387cd3ad12", "nav.prospect_overview"],
  ["7e9b5df9-45bc-45c8-a49e-c74da642d445", "nav.prospect_quote"],
  ["a3b5dc9c-f741-4667-8639-d2e5b7eb057c", "nav.prospect_list"],
  ["9796f640-2726-412a-8105-919e8a6d8218", "nav.prospect_calendar"],
  ["05a8334c-0c8b-4d51-8dec-34a3b3ebc5f8", "nav.prospect_followup"],
  ["91212b15-6de7-47d6-ab95-c2eb458256e6", "nav.prospect_canned_msg"],
  ["c6f84aba-46ee-4d58-bc89-53da30964d16", "nav.prospect_saved_filter"],
  ["e6e96d31-cb30-4b74-852a-d7edc33efe4e", "nav.prospect_auto_msg_setting"],
  ["1cf4b2e1-6a8a-416a-89b5-c0773fd21c34", "nav.prospect_store_setting"],
  ["5dcdfc8b-64a3-4e16-a760-b6eb6a32553c", "nav.prospect_quick_new"],
  ["8fbcad10-c3fc-4630-87b3-f46425201603", "nav.prospect_frequently_filter_list"],
  ["66a80284-8356-44b3-9989-aa22417840d8", "nav.prospect_exhibitions"],
  ["d4e395ab-9d7a-4359-8ceb-07483a771a63", "nav.prospect_store_activity"],
  ["062beea4-854f-48b0-b0ff-028c06c1a99f", "nav.order_pending_order"],
  ["7ade99ab-f28f-41e9-99b9-dd413705f213", "nav.order_delivery_calendar"],
  ["ecf9fd18-50c4-444f-a57c-138bc20280f3", "nav.order_handover"],
  ["23118cc8-6f99-49b0-9960-764232f15cdd", "nav.order_batch_order"],
  ["10b92c61-5d22-413f-83b8-5c9864ae93e5", "nav.allocation"],
  ["c028816a-f08f-4d7d-b352-b5b91c608b65", "nav.allocation_reservations"],
  ["15dacb24-fbe8-40a0-a45f-4f0fc38ed798", "nav.allocation_orders"],
  ["0398d6a8-73a4-40d8-b557-d305ae39ec5b", "nav.allocation_warehouses"],
  ["eee1f69d-3d0c-4f93-8203-a6a189cf875b", "nav.allocation_rules"],
  ["9bcc9e61-829a-4fc5-a4e1-fc946ff90212", "nav.allocation_companies"],
  ["125eb72f-df02-4a39-aef9-5742592ecfa1", "nav.allocation_reports"],
  ["00a0e071-97cd-4f4b-9ed6-830268fee91f", "nav.allocation_franchisees"],
  ["b1d0466e-c5e2-486d-87e1-81fb6c4440dd", "nav.tartget_achievement_target_setting"],
  ["b1e252bc-f76d-49be-9b60-d26784608faf", "nav.tartget_achievement_report"],
  ["8213d6dc-0d1e-42a4-82ab-3ab55e98481c", "nav.tartget_achievement_estimated"],
  ["1a24be9a-4d0b-417f-99fa-ae2ca907165b", "nav.tartget_achievement_daily_ratio"],
  ["8e599408-6b09-407e-9ce4-f78193c0dce4", "nav.configs"],
  ["710ae89b-60e8-4344-9eed-579d7b236c93", "nav.configs_delivery_schedule"],
  ["4fcfca84-9de1-43ca-9cd1-3a551dbb172a", "nav.configs_scooter_sing_date"],
  ["389ffc34-e4d8-454f-9a2f-6883ee8d661a", "nav.configs_discounts"],
  ["1342f70e-9b20-40ad-925f-8d9b440f4d13", "nav.configs_coupons"],
  ["82d2d643-174d-4823-b818-991fe71143eb", "nav.load_data"],
  ["a9ae9aef-29fc-4a66-90e8-51df2e62d8ed", "nav.points_report"],
  ["9532cd47-332e-4cd5-9e92-37c88189ec5c", "nav.search_scooters"],
  ["fa2b2b48-a917-4c16-9ee4-e48a4bc84ca6", "nav.my_profile"],
])

export const useSales = () => {
  const user = useUser()
  const { Menus } = user || {}

  return useMemo(
    () => ({
      name: "sales",
      title: "Sales Portal",
      clientCode: "sales_portal",
      shortcuts: (() => {
        return Menus
          ? [
              { id: "testRide", link: "/testride/2", title: "nav.testride" },
              { id: "prospect", link: "/prospect/index", title: "nav.prospect" },
              { id: "smartscooter", link: "/scooter", title: "nav.scooter" },
              { id: "orderManage", link: "/order/manage", title: "nav.order" },
              { id: "accessory", link: "/store", title: "nav.acc" },
              { id: "transfer", link: "/ownership/transfer", title: "nav.transfer" },
            ].filter((entry) => Menus.find((item) => item.Url === entry.link))
          : []
      })(),
      entries: Menus
        ? Menus.filter((entry) => !entry.ParentId).map((entry) => {
            const subEntries = Menus.filter((item) => item.ParentId === entry.Id)
            // custom prospect test ride route
            return {
              title: menuIdMap.get(entry.Id) || entry.Name,
              path: entry.Url,
              icon: iconsMap[entry.Url] || (subEntries.length ? undefined : "file"),
              entries: subEntries.length
                ? subEntries.map((entry) => ({
                    title: menuIdMap.get(entry.Id) || entry.Name,
                    path:
                      entry.Id === "1cf4b2e1-6a8a-416a-89b5-c0773fd21c34"
                        ? "/prospect/reservations/test-ride-settings"
                        : entry.Url,
                  }))
                : undefined,
            }
          })
        : [],
    }),
    [Menus],
  )
}

const Sales = () => {
  const dispatch = useDispatch()
  const user = useUser()
  const { logOut } = useAuth()

  const { loadingRef } = useContext(OverlayContext)
  const { openWarningModal } = useDialogs()
  const reduxUser = useSelector((state) => state.userState.userData)
  const modalRef = useSelector((state) => state.globalUi.modal)
  const isLoading = useSelector((state) => state.globalUi.isLoading)
  const { isGlobe } = useGlobe()
  const dataCompany = useDataCompany()

  useEffect(() => {
    dispatch(userStateAction.setIsGlobe(isGlobe))
    dispatch(userStateAction.setDataCompany(dataCompany))
  }, [dispatch, isGlobe, dataCompany])

  useEffect(() => {
    // TODO replace it with useLoadingIndicator
    loadingRef.current[isLoading ? "open" : "close"]()
  }, [loadingRef, isLoading])

  useEffect(() => {
    if (user) {
      dispatch(userStateAction.setUserState({ userData: user }))
    }

    // 確認是否支援cookies
    if (!supported.isCookieSupported()) {
      openWarningModal({
        title: "提醒",
        content: "你的瀏覽器已停用 Cookie，為了獲得更完整資訊及完成購買，請啟用 Cookie 功能。",
      })
    }
    // 確認是否支援LocalStorage
    if (!supported.isLocalStorageSupported()) {
      openWarningModal({
        title: "提醒",
        content: "你使用的瀏覽器（Safari 私密瀏覽模式）無法使用暫存功能，請關閉私密瀏覽模式或改用 Chrome 瀏覽器。",
      })
    }
  }, [user, dispatch, openWarningModal])

  useIdleTimer({
    timeout: 1000 * 60 * 60 * 2, //logout after 2 hr
    disabled: user === null, // check that if the user has logged in
    onIdle: () => logOut().then(() => (window.location.href = "/")),
  })

  return reduxUser && modalRef ? (
    <>
      <ProspectComponent />
      <Routes>
        <Route path="tw/*" element={<StripTW />} />

        <Route path="prospect/*" element={<ProspectPages />} />

        <Route path="exhibitions" element={<ExhibitionsPage />} />

        <Route path="allocations/*" element={<AllocationPages />} />

        <Route path="store" element={<Store />} />

        <Route path="search/result" element={<Search />} />

        <Route path="product/detail" element={<Product />}>
          <Route path=":productId" />
        </Route>

        <Route path="scooter" element={<Scooter />} />

        <Route path="order/*" element={<OrderRoutes />} />
        <Route path="orders/*" element={<OrderRoutes />} />

        <Route path="ownership/batterybill" element={<BatteryBill />}>
          <Route path=":plateNo" />
        </Route>

        <Route path="ownership/transfer" element={<Navigate to="/transfer" replace={true} />} />

        <Route path="transfer/*" element={<TransferRoutes />} />

        <Route path="testride" element={<TestRide />}>
          <Route path=":contractType" />
        </Route>

        <Route path="configs/*" element={<ConfigRoutes />} />

        <Route path="loan-data" element={<LoansReport />} />

        <Route path="points-report" element={<PointsReport />} />

        <Route path="scooters" element={<ScooterPage />}>
          <Route path=":number" />
        </Route>

        <Route path="my-profile" element={<MyProfileRoutes />} />

        <Route path="*" element={<QuickMenu />} />
      </Routes>
    </>
  ) : null
}

const store = configureStore()

const ModalEffect = () => {
  // used for redux side effect, should replace with useDialogs hook
  const dispatch = useDispatch()
  const { dialogRef } = useContext(OverlayContext)

  useEffect(() => {
    dispatch(globalUiAction.setGlobalUI({ modal: dialogRef }))
  }, [dispatch, dialogRef])

  return null
}

const ReduxContext = () => {
  return (
    <Provider store={store}>
      <ModalEffect />
      <Sales />
    </Provider>
  )
}

export default ReduxContext
